import { Component, ChangeDetectionStrategy, output, inject, signal, OnInit, OnDestroy, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Plan } from '../../models/subscription.model';
import { AuthService } from '../../services/auth.service';
import { PaymentService } from '../../services/payment.service';
import { PublicDataService } from '../../services/public-data.service';
import { CouponService } from '../../services/coupon.service';

type PerformanceBar = {
  label: string;
  value: string;
  progress: number;
};

type ValueCard = {
  eyebrow: string;
  title: string;
  description: string;
};

type WorkflowStep = {
  title: string;
  description: string;
};

@Component({
  selector: 'app-landing-page',
  standalone: true,
  template: `
<div class="landing-canvas relative overflow-hidden bg-slate-950 text-slate-100 [background-image:radial-gradient(circle_at_12%_8%,rgba(37,99,235,.28),transparent_35%),radial-gradient(circle_at_88%_22%,rgba(6,182,212,.2),transparent_33%),radial-gradient(circle_at_50%_92%,rgba(59,130,246,.18),transparent_36%)]">
  <div class="pointer-events-none absolute inset-0">
    <div class="ambient-float-slow absolute -left-40 top-20 h-[360px] w-[360px] rounded-full bg-blue-600/14 blur-3xl"></div>
    <div class="ambient-float-medium absolute -right-36 top-[30%] h-[360px] w-[360px] rounded-full bg-indigo-600/16 blur-3xl"></div>
    <div class="ambient-float-fast absolute left-[35%] top-[48%] h-[320px] w-[320px] rounded-full bg-blue-500/10 blur-3xl"></div>
    <div class="ambient-float-medium absolute left-[25%] bottom-[-160px] h-[320px] w-[320px] rounded-full bg-cyan-500/12 blur-3xl"></div>
    <div class="grid-drift absolute inset-0 opacity-[0.08] [background-image:linear-gradient(rgba(56,189,248,0.35)_1px,transparent_1px),linear-gradient(90deg,rgba(56,189,248,0.35)_1px,transparent_1px)] [background-size:120px_120px]"></div>
    <div class="absolute inset-0 bg-gradient-to-b from-slate-950/45 via-slate-950/30 to-slate-950/70"></div>
  </div>

  <section class="relative">
    <div class="container mx-auto px-6 py-16 lg:py-20">
      <div class="grid items-center gap-10 lg:grid-cols-[1.08fr_0.92fr]">
        <div>
          <p class="inline-flex rounded-full border border-blue-400/30 bg-blue-500/10 px-4 py-1 text-xs font-bold uppercase tracking-widest text-blue-200">
            Mentoria de aprovação por banca
          </p>

          <h1 class="mt-6 text-4xl font-black leading-tight text-white md:text-6xl">
            Não é só correção. É estratégia para subir nota na banca certa.
          </h1>

          <p class="mt-6 max-w-2xl text-lg text-slate-300">
            O Correctify transforma cada envio em decisão de estudo: meta de nota com prazo, diagnóstico orientado por banca e próxima ação prática para evoluir.
          </p>

          <div class="mt-10 flex flex-col gap-4 sm:flex-row">
            <button
              (click)="onGetStarted()"
              class="cta-glow rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-9 py-4 text-base font-bold text-white shadow-lg shadow-blue-900/35 transition hover:opacity-90">
              Começar grátis
            </button>
            <button
              (click)="onGoToLogin()"
              class="rounded-xl border border-slate-700 bg-slate-900/70 px-8 py-4 text-base font-bold text-slate-200 transition hover:border-slate-500 hover:text-white">
              Já tenho conta
            </button>
          </div>

          <div class="mt-8 grid grid-cols-1 gap-3 text-sm text-slate-300 sm:grid-cols-3">
            <div class="rounded-xl border border-slate-800 bg-slate-900/70 px-4 py-3">
              <p class="font-semibold text-white">Meta + prazo</p>
              <p class="mt-1 text-slate-400">Evolução previsível até a prova</p>
            </div>
            <div class="rounded-xl border border-slate-800 bg-slate-900/70 px-4 py-3">
              <p class="font-semibold text-white">Diagnóstico por banca</p>
              <p class="mt-1 text-slate-400">Foco no critério que mais derruba nota</p>
            </div>
            <div class="rounded-xl border border-slate-800 bg-slate-900/70 px-4 py-3">
              <p class="font-semibold text-white">Próxima ação objetiva</p>
              <p class="mt-1 text-slate-400">Tarefa de 15 a 20 minutos por envio</p>
            </div>
          </div>
        </div>

        <article class="hero-console rounded-3xl border border-slate-700/70 bg-slate-900/85 p-6 shadow-2xl backdrop-blur">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-xs uppercase tracking-widest text-blue-300">Mentoria orientada por banca</p>
              <h2 class="mt-1 text-xl font-black text-white">Meta de nota com plano semanal</h2>
            </div>
            <span class="rounded-full border border-emerald-400/30 bg-emerald-500/10 px-3 py-1 text-xs font-bold text-emerald-300">
              Em dia
            </span>
          </div>

          <div class="mt-6 grid grid-cols-3 gap-3">
            <div class="rounded-xl border border-slate-700 bg-slate-950/75 p-3">
              <p class="text-xs text-slate-400">Nota atual</p>
              <p class="mt-1 text-lg font-black text-white">860</p>
            </div>
            <div class="rounded-xl border border-slate-700 bg-slate-950/75 p-3">
              <p class="text-xs text-slate-400">Meta</p>
              <p class="mt-1 text-lg font-black text-white">920</p>
            </div>
            <div class="rounded-xl border border-slate-700 bg-slate-950/75 p-3">
              <p class="text-xs text-slate-400">Prazo</p>
              <p class="mt-1 text-lg font-black text-white">6 sem</p>
            </div>
          </div>

          <div class="mt-6 space-y-4">
            @for (bar of performanceBars; track bar.label) {
              <div>
                <div class="mb-1 flex items-center justify-between text-sm">
                  <span class="text-slate-300">{{ bar.label }}</span>
                  <span class="font-semibold text-white">{{ bar.value }}</span>
                </div>
                <div class="h-2 rounded-full bg-slate-800">
                  <div class="h-2 rounded-full bg-gradient-to-r from-blue-500 to-cyan-400" [style.width.%]="bar.progress"></div>
                </div>
              </div>
            }
          </div>

          <div class="mt-6 rounded-xl border border-slate-700 bg-slate-950/75 p-4">
            <p class="text-xs uppercase tracking-wide text-slate-400">Próxima ação (15 a 20 min)</p>
            <p class="mt-2 text-sm text-slate-200">
              Reescreva o desenvolvimento 2 com repertório validado e conectores de progressão para elevar a consistência argumentativa.
            </p>
          </div>
        </article>
      </div>
    </div>
  </section>

  <section class="relative py-14 lg:py-16">
    <div class="container mx-auto px-6">
      <div class="mb-10 text-center">
        <h2 class="text-3xl font-black text-white md:text-4xl">Por que essa mentoria gera nota</h2>
        <p class="mx-auto mt-3 max-w-2xl text-slate-400">
          Método focado na banca, com rotina acionável e acompanhamento real da curva de evolução.
        </p>
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        @for (card of valueCards; track card.title) {
          <article class="tech-card rounded-2xl border border-slate-700/80 bg-gradient-to-br from-slate-900/85 via-slate-900/70 to-blue-950/20 p-6 transition hover:border-blue-400/60">
            <p class="text-sm text-blue-300">{{ card.eyebrow }}</p>
            <h3 class="mt-2 text-xl font-bold text-white">{{ card.title }}</h3>
            <p class="mt-2 text-sm leading-relaxed text-slate-400">{{ card.description }}</p>
          </article>
        }
      </div>
    </div>
  </section>

  <section class="relative py-14 lg:py-16">
    <div class="container mx-auto px-6">
      <div class="mb-10 text-center">
        <h2 class="text-3xl font-black text-white md:text-4xl">Ciclo de evolução até a prova</h2>
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        @for (step of workflowSteps; track step.title; let index = $index) {
          <article class="tech-card rounded-2xl border border-slate-700/90 bg-gradient-to-b from-slate-900/85 to-slate-950/85 p-6">
            <span class="inline-flex h-9 w-9 items-center justify-center rounded-full border border-blue-400/40 bg-blue-500/15 text-sm font-black text-blue-300">
              {{ index + 1 }}
            </span>
            <h3 class="mt-4 text-lg font-bold text-white">{{ step.title }}</h3>
            <p class="mt-2 text-sm text-slate-400">{{ step.description }}</p>
          </article>
        }
      </div>
    </div>
  </section>

  <section class="relative py-14 lg:py-16" id="plans">
    <div class="container mx-auto px-4">
      <div class="mb-10 text-center">
        <h2 class="text-3xl font-black text-white md:text-4xl">Planos para cada fase da preparação</h2>
        <p class="mx-auto mt-3 max-w-2xl text-slate-400">
          Comece no seu ritmo e evolua com clareza de meta, prioridade e progresso.
        </p>
      </div>

      @if (isLoadingPlans()) {
        <div class="text-center">
          <div class="loader mx-auto"></div>
          <p class="mt-4 text-slate-400">Carregando planos...</p>
        </div>
      } @else if (plans().length === 0) {
        <div class="mx-auto max-w-xl rounded-2xl border border-slate-700 bg-slate-900 p-8 text-center">
          <p class="text-lg font-semibold text-white">Não foi possível carregar os planos agora.</p>
          <p class="mt-2 text-sm text-slate-400">Tente novamente sem sair da página.</p>
          <button (click)="reloadPlans()" class="mt-6 rounded-lg bg-blue-600 px-6 py-3 font-bold text-white transition hover:bg-blue-500">
            Tentar novamente
          </button>
        </div>
      } @else {
        <div class="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-4">
          @for (plan of planFeatures(); track plan.id) {
            <article
              class="tech-card relative flex h-full flex-col rounded-2xl border p-6 transition hover:-translate-y-1"
              [ngClass]="isMostPopular(plan)
                ? 'border-blue-400 bg-gradient-to-b from-slate-900 to-blue-950/30 shadow-xl shadow-blue-900/25'
                : 'border-slate-700/90 bg-gradient-to-b from-slate-900/85 to-slate-950/85'">

              @if (isMostPopular(plan)) {
                <span class="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-blue-500 px-3 py-1 text-xs font-black uppercase tracking-wide text-white">
                  Mais escolhido
                </span>
              }

              <div class="flex-grow">
                <h3 class="text-2xl font-black text-white">{{ plan.name }}</h3>
                <p class="mt-2 text-sm text-slate-400">{{ plan.description }}</p>

                <div class="my-6">
                  @if (plan.price === 0) {
                    <span class="text-4xl font-black text-white">Grátis</span>
                  } @else {
                    <span class="text-4xl font-black text-white">R$ {{ (plan.price / 100).toFixed(2).replace('.', ',') }}</span>
                    <span class="text-sm text-slate-400">{{ getPlanIntervalLabel(plan) }}</span>
                  }
                </div>

                <ul class="space-y-2 text-sm text-slate-300">
                  @for (feature of plan.features; track feature) {
                    <li class="flex items-start">
                      <svg class="mr-2 mt-0.5 h-4 w-4 flex-shrink-0 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.86-9.81a.75.75 0 0 0-1.21-.88l-3.49 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.06l2.5 2.5a.75.75 0 0 0 1.06 0l4-5.5Z" clip-rule="evenodd" />
                      </svg>
                      <span>{{ feature }}</span>
                    </li>
                  }
                </ul>
              </div>

              <button
                (click)="onSelectPlan(plan)"
                class="cta-glow mt-8 w-full rounded-xl px-4 py-3 font-bold transition"
                [class.bg-blue-600]="plan.price > 0"
                [class.text-white]="plan.price > 0"
                [class.hover:bg-blue-500]="plan.price > 0"
                [class.bg-slate-100]="plan.price === 0"
                [class.text-slate-900]="plan.price === 0"
                [class.hover:bg-white]="plan.price === 0">
                @if (plan.price === 0) { Testar grátis } @else { Assinar agora }
              </button>
            </article>
          }
        </div>
      }
    </div>
  </section>

  <section class="relative py-14 lg:py-16">
    <div class="container mx-auto px-6">
      <div class="mb-10 text-center">
        <h2 class="text-3xl font-black text-white md:text-4xl">Quem já usa, evolui</h2>
        <p class="mx-auto mt-3 max-w-2xl text-slate-400">
          Veja o que concurseiros estão dizendo sobre a experiência com o Correctify.
        </p>
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        @for (testimonial of testimonials; track testimonial.name) {
          <article class="tech-card rounded-2xl border border-slate-700/80 bg-gradient-to-br from-slate-900/85 via-slate-900/70 to-blue-950/20 p-6">
            <div class="flex items-center gap-1 text-yellow-400">
              @for (star of [1,2,3,4,5]; track star) {
                <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
              }
            </div>
            <p class="mt-4 text-sm leading-relaxed text-slate-300">{{ testimonial.text }}</p>
            <div class="mt-4 border-t border-slate-700/60 pt-3">
              <p class="text-sm font-semibold text-white">{{ testimonial.name }}</p>
              <p class="text-xs text-slate-400">{{ testimonial.role }}</p>
            </div>
          </article>
        }
      </div>
    </div>
  </section>

  <section class="relative py-14 lg:py-16">
    <div class="container mx-auto px-6">
      <div class="mx-auto max-w-4xl rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-900 to-slate-950 p-8 text-center shadow-xl md:p-10">
        <p class="text-xs uppercase tracking-[0.18em] text-blue-300">Pronto para acelerar sua preparação</p>
        <h2 class="mt-4 text-3xl font-black text-white md:text-4xl">Entre hoje e evolua com clareza de meta e método</h2>
        <p class="mx-auto mt-4 max-w-2xl text-slate-400">
          Em poucos minutos, você ativa uma rotina de evolução com foco na banca e acompanhamento contínuo.
        </p>
        <div class="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
          <button (click)="onGetStarted()" class="cta-glow rounded-xl bg-blue-600 px-8 py-3.5 font-bold text-white transition hover:bg-blue-500">
            Criar conta
          </button>
          <button (click)="onGoToLogin()" class="rounded-xl border border-slate-700 px-8 py-3.5 font-bold text-slate-200 transition hover:border-slate-500 hover:text-white">
            Entrar
          </button>
        </div>
      </div>
    </div>
  </section>

  <section class="relative pb-16 lg:pb-20">
    <div class="container mx-auto px-6">
      <div class="mb-10 text-center">
        <h2 class="text-3xl font-black text-white md:text-4xl">Perguntas frequentes</h2>
      </div>

      <div class="mx-auto max-w-3xl space-y-4">
        <details class="tech-card rounded-xl border border-slate-700/80 bg-gradient-to-br from-slate-900/85 to-blue-950/20 p-5">
          <summary class="cursor-pointer font-semibold text-white">A análise é confiável para concurso?</summary>
          <p class="mt-3 text-sm text-slate-400">
            Sim. O fluxo foi estruturado para refletir critérios técnicos de correção usados em contexto de concurso.
          </p>
        </details>

        <details class="tech-card rounded-xl border border-slate-700/80 bg-gradient-to-br from-slate-900/85 to-blue-950/20 p-5">
          <summary class="cursor-pointer font-semibold text-white">Posso cancelar quando quiser?</summary>
          <p class="mt-3 text-sm text-slate-400">
            Sim. Você pode cancelar a assinatura a qualquer momento, sem burocracia.
          </p>
        </details>

        <details class="tech-card rounded-xl border border-slate-700/80 bg-gradient-to-br from-slate-900/85 to-blue-950/20 p-5">
          <summary class="cursor-pointer font-semibold text-white">Como funciona o plano gratuito?</summary>
          <p class="mt-3 text-sm text-slate-400">
            Ao criar a conta, você recebe crédito inicial para experimentar o fluxo da plataforma.
          </p>
        </details>

        <details class="tech-card rounded-xl border border-slate-700/80 bg-gradient-to-br from-slate-900/85 to-blue-950/20 p-5">
          <summary class="cursor-pointer font-semibold text-white">Quais bancas são suportadas?</summary>
          <p class="mt-3 text-sm text-slate-400">
            Atualmente a plataforma suporta FCC, FGV, Cebraspe e ENEM, cada uma com critérios técnicos próprios da banca.
          </p>
        </details>

        <details class="tech-card rounded-xl border border-slate-700/80 bg-gradient-to-br from-slate-900/85 to-blue-950/20 p-5">
          <summary class="cursor-pointer font-semibold text-white">A correção substitui um professor?</summary>
          <p class="mt-3 text-sm text-slate-400">
            O Correctify é um complemento inteligente: diagnóstico rápido, feedback técnico e ação prática entre cada envio. Para preparação intensiva, combine com orientação humana.
          </p>
        </details>

        <details class="tech-card rounded-xl border border-slate-700/80 bg-gradient-to-br from-slate-900/85 to-blue-950/20 p-5">
          <summary class="cursor-pointer font-semibold text-white">Meus dados estão protegidos?</summary>
          <p class="mt-3 text-sm text-slate-400">
            Sim. Seguimos a LGPD com consentimento explícito, exportação de dados, exclusão de conta e criptografia em trânsito e repouso.
          </p>
        </details>
      </div>
    </div>
  </section>

  <section class="relative pb-16 lg:pb-20">
    <div class="container mx-auto px-6">
      <div class="mx-auto max-w-2xl rounded-2xl border border-emerald-400/30 bg-emerald-500/5 p-8 text-center">
        <div class="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15">
          <svg class="h-7 w-7 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
          </svg>
        </div>
        <h3 class="mt-4 text-xl font-black text-white">Garantia de satisfação</h3>
        <p class="mt-3 text-sm text-slate-300">
          Teste o plano gratuito sem compromisso. Se assinar e não gostar, cancele em até 7 dias e entraremos em contato para resolver.
        </p>
      </div>
    </div>
  </section>
</div>`,
  styles: [`
    .landing-canvas {
      isolation: isolate;
    }

    .grid-drift {
      animation: gridDrift 22s linear infinite;
      will-change: transform;
    }

    .ambient-float-slow {
      animation: ambientFloatSlow 14s ease-in-out infinite;
      will-change: transform;
    }

    .ambient-float-medium {
      animation: ambientFloatMedium 10s ease-in-out infinite;
      will-change: transform;
    }

    .ambient-float-fast {
      animation: ambientFloatFast 8s ease-in-out infinite;
      will-change: transform;
    }

    .hero-console {
      position: relative;
      overflow: hidden;
      animation: panelFloat 9s ease-in-out infinite;
    }

    .hero-console::before {
      content: '';
      position: absolute;
      inset: 0;
      pointer-events: none;
      background: linear-gradient(120deg, transparent 15%, rgba(56, 189, 248, 0.09) 48%, transparent 82%);
      transform: translateX(-120%);
      animation: sweep 8.5s ease-in-out infinite;
    }

    .tech-card {
      position: relative;
      overflow: hidden;
      transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
    }

    .tech-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 16px 36px -24px rgba(37, 99, 235, 0.65);
    }

    .tech-card::after {
      content: '';
      position: absolute;
      inset: 0;
      pointer-events: none;
      background: linear-gradient(140deg, rgba(96, 165, 250, 0.08), transparent 45%);
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    .tech-card:hover::after {
      opacity: 1;
    }

    .cta-glow {
      position: relative;
      overflow: hidden;
    }

    .cta-glow::after {
      content: '';
      position: absolute;
      top: -120%;
      left: -60%;
      width: 38%;
      height: 320%;
      pointer-events: none;
      background: linear-gradient(180deg, transparent, rgba(255, 255, 255, 0.45), transparent);
      transform: translateX(0) rotate(24deg);
      opacity: 0;
      transition: transform 0.75s ease, opacity 0.3s ease;
    }

    .cta-glow:hover::after {
      transform: translateX(440%) rotate(24deg);
      opacity: 0.55;
    }

    @keyframes gridDrift {
      0% {
        transform: translate3d(0, 0, 0);
      }
      100% {
        transform: translate3d(48px, 48px, 0);
      }
    }

    @keyframes ambientFloatSlow {
      0%, 100% {
        transform: translate3d(0, 0, 0);
      }
      50% {
        transform: translate3d(0, -18px, 0);
      }
    }

    @keyframes ambientFloatMedium {
      0%, 100% {
        transform: translate3d(0, 0, 0);
      }
      50% {
        transform: translate3d(12px, -10px, 0);
      }
    }

    @keyframes ambientFloatFast {
      0%, 100% {
        transform: translate3d(0, 0, 0);
      }
      50% {
        transform: translate3d(-8px, -12px, 0);
      }
    }

    @keyframes panelFloat {
      0%, 100% {
        transform: translate3d(0, 0, 0);
      }
      50% {
        transform: translate3d(0, -6px, 0);
      }
    }

    @keyframes sweep {
      0% {
        transform: translateX(-120%);
      }
      30%, 100% {
        transform: translateX(150%);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .grid-drift,
      .ambient-float-slow,
      .ambient-float-medium,
      .ambient-float-fast,
      .hero-console,
      .hero-console::before {
        animation: none;
      }

      .tech-card,
      .tech-card::after,
      .cta-glow::after {
        transition: none;
      }
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
})
export class LandingPageComponent implements OnInit, OnDestroy {
  requestRegister = output<void>();
  requestLogin = output<void>();

  private publicDataService = inject(PublicDataService);
  private authService = inject(AuthService);
  private paymentService = inject(PaymentService);
  private couponService = inject(CouponService);

  readonly plans = signal<Plan[]>([]);
  readonly isLoadingPlans = signal(true);
  readonly isAuthenticated = this.authService.isAuthenticated;
  readonly activeCouponCode = this.couponService.activeCouponCode;
  private stopPlansWatch?: () => void;

  readonly performanceBars: PerformanceBar[] = [
    { label: 'Domínio normativo', value: '82%', progress: 82 },
    { label: 'Argumentação', value: '78%', progress: 78 },
    { label: 'Coesão e coerência', value: '85%', progress: 85 },
  ];

  readonly testimonials = [
    {
      name: 'Carolina M.',
      role: 'Aprovada – TRT (FCC)',
      text: 'Antes eu estudava redação sem saber onde estava errando. Com o Correctify, cada correção virou um plano de ação claro. Subi 1.5 ponto em 3 meses.',
    },
    {
      name: 'Rafael S.',
      role: 'Concurseiro – Polícia Federal (Cebraspe)',
      text: 'O diagnóstico por banca faz toda a diferença. Parei de perder tempo com dicas genéricas e foquei no que realmente derrubava minha nota.',
    },
    {
      name: 'Juliana T.',
      role: 'Estudante – ENEM 2025',
      text: 'Consegui entender exatamente o que faltava na minha Competência 5. O feedback é técnico mas fácil de entender. Recomendo muito.',
    },
  ];

  readonly valueCards: ValueCard[] = [
    {
      eyebrow: 'Meta de aprovação',
      title: 'Nota alvo com prazo estimado',
      description: 'Cada ciclo define seu ponto de partida, meta de nota e janela realista para evolução.',
    },
    {
      eyebrow: 'Banca em primeiro lugar',
      title: 'Diagnóstico alinhado ao critério real',
      description: 'A análise prioriza os pontos que mais pesam na sua banca e evita estudo disperso.',
    },
    {
      eyebrow: 'Ação objetiva',
      title: 'Próximo passo claro a cada redação',
      description: 'Você recebe uma tarefa prática e curta para transformar diagnóstico em ganho de nota.',
    },
  ];

  readonly workflowSteps: WorkflowStep[] = [
    {
      title: 'Envie sua redação',
      description: 'Defina tema e banca para ativar uma avaliação alinhada ao padrão da prova.',
    },
    {
      title: 'Receba o diagnóstico técnico',
      description: 'Veja critério por critério e identifique o gargalo que mais reduz sua pontuação hoje.',
    },
    {
      title: 'Aplique e evolua',
      description: 'Execute a ação recomendada e acompanhe a subida de nota no histórico.',
    },
  ];

  readonly planFeatures = computed(() => {
    return this.plans().map((plan) => {
      const planName = this.normalizePlanName(plan.name);
      let features: string[] = [];

      if (planName.includes('gratuito')) {
        features = ['1 correção por mês', 'Análise básica', 'Análise por banca', 'Sem histórico completo'];
      } else if (planName.includes('basico')) {
        features = [`${plan.credits_granted} correções por mês`, 'Análise padrão', 'Histórico das 10 últimas redações'];
      } else if (planName.includes('intermediario')) {
        features = [`${plan.credits_granted} correções por mês`, 'Análise avançada', 'Histórico ilimitado', 'Diagnóstico simplificado'];
      } else if (planName.includes('pro')) {
        features = [
          'Correções ilimitadas',
          'Análise completa em 5 camadas',
          'Histórico ilimitado',
          'Plano de evolução com IA',
          'Reescrita sugerida',
        ];
      }

      if (features.length === 0) {
        features = plan.price === 0
          ? ['Plano inicial para conhecer a plataforma', 'Análise essencial para iniciar']
          : [`${plan.credits_granted} correções por ciclo`, 'Feedback técnico por critério', 'Histórico de desempenho'];
      }

      return { ...plan, features };
    });
  });

  private normalizePlanName(name: string): string {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '');
  }

  async ngOnInit() {
    await this.reloadPlans();
    this.stopPlansWatch = this.publicDataService.watchPlansUpdates(() => {
      void this.reloadPlans(true);
    });
  }

  ngOnDestroy(): void {
    this.stopPlansWatch?.();
  }

  async reloadPlans(silent = false) {
    this.isLoadingPlans.set(true);
    try {
      const plansData = await this.publicDataService.getPlans();
      const filteredPlans = plansData.filter((p) => p.interval !== 'credits');
      this.plans.set(filteredPlans.sort((a, b) => a.price - b.price));
    } catch {
      this.plans.set([]);
      if (!silent) {
        this.authService.showNotification('error', 'Falha ao carregar planos', 'Não foi possível buscar os planos no momento.');
      }
    } finally {
      this.isLoadingPlans.set(false);
    }
  }

  onGetStarted() {
    this.requestRegister.emit();
  }

  onGoToLogin() {
    this.requestLogin.emit();
  }

  getPlanIntervalLabel(plan: Plan): string {
    switch (plan.interval) {
      case 'month':
        return '/mês';
      case 'quarter':
        return '/trimestre';
      case 'year':
        return '/ano';
      default:
        return '';
    }
  }

  async onSelectPlan(plan: Plan) {
    if (this.isAuthenticated()) {
      if (plan.price > 0) {
        await this.paymentService.startCheckout(String(plan.id), { preopen: true, couponCode: this.activeCouponCode() });
      } else {
        this.authService.showNotification('info', 'Plano gratuito', 'Sua conta já possui os benefícios do plano gratuito.');
      }
      return;
    }

    if (plan.price > 0) {
      this.authService.setPendingPriceId(String(plan.id));
      this.authService.showNotification('info', 'Faça login para continuar', 'Entre na sua conta para prosseguir com a assinatura.');
      this.requestLogin.emit();
      return;
    }

    this.requestRegister.emit();
  }

  isMostPopular(plan: Plan): boolean {
    const name = this.normalizePlanName(plan.name);
    return name.includes('intermediario');
  }
}
